/* eslint-disable no-process-env */
// `NEXT_PUBLIC_*` values are inlined at build time, so they have to be read off `process.env`
// directly -- the same exemption `global-auth-proxy/api/global.ts` takes for the Hydra base URL.

/**
 * LevelUp's own portfolio, where an investor goes after investing and from the branded user menu.
 *
 * Read from the environment because it differs per environment, and because the staging Cloud Run
 * URL it replaced was hardcoded in two places -- both of which ship to production.
 */
export const levelupPortfolioUrl = (): string | undefined =>
  process.env.NEXT_PUBLIC_LEVELUP_PORTFOLIO_URL;

/**
 * LevelUp's own homepage, linked from the brand logo on the auth and invest headers. Hardcoded
 * as a brand constant rather than derived per offering: the logo is a static brand affordance,
 * present on auth pages that have no offering to source `website_url` from. `www.` because the
 * apex only redirects at `/`.
 */
export const LEVELUP_HOME_URL = 'https://www.levelupsports.com';

/**
 * LevelUp's own logout endpoint, hit as a top-level navigation from the branded menu's "Log out".
 *
 * Ends LevelUp's session (globally, across the user's devices) and 302s the browser to a page
 * on LevelUp's own site -- LevelUp owns that landing. It deliberately does NOT touch the Republic
 * session: signing out of the partner leaves the user's own Republic session (web, mobile apps,
 * wallet) intact, which is the conventional "log out of the relying party, not the identity
 * provider" behaviour. Must be a plain link/`location` navigation -- a `fetch` clears nothing and
 * an iframe is storage-partitioned.
 *
 * A per-environment constant rather than derived from `website_url`: this is LevelUp's *api* host,
 * not the `www` site the issuer field names, so there is nothing to derive it from.
 */
export const levelupLogoutUrl = (applicationEnvironment?: string): string =>
  applicationEnvironment === 'production'
    ? 'https://api.levelupsports.com/api/v1/auth/logout'
    : 'https://level-up-backend-606592623818.us-central1.run.app/api/v1/auth/logout';

/**
 * LevelUp's "start a Republic auth journey" endpoint — the same one LevelUp's own site hits to
 * begin the OIDC handshake. We send a branded user here when they finish sign-up/sign-in on a
 * branded page *outside* an OIDC flow (no `login_challenge` to continue), so the journey still
 * ends on LevelUp rather than on Republic onboarding or the Republic homepage.
 *
 * Because the user already has a live Republic session at this point, re-entering the handshake
 * completes silently (SSO): Republic accepts it without showing any auth UI, hands LevelUp a
 * verifier, and LevelUp mints its own session and lands the user on `next` — so they arrive on
 * LevelUp genuinely logged in. Must be a top-level navigation (cross-origin, absolute URL).
 *
 * Only `next` is sent. The `intent` the LevelUp site normally adds is read Republic-side to choose
 * the register-vs-login view, which a signed-in visitor never reaches (the OIDC continuation
 * short-circuits first), so it is omitted. LevelUp's own `lu_aid` attribution id is omitted too --
 * it is set on LevelUp's frontend and Republic never receives it.
 *
 * A per-environment constant rather than derived from `website_url`: this is LevelUp's *api* host
 * (same split as `levelupLogoutUrl`), not the `www` site the issuer field names.
 */
export const levelupStartUrl = (
  applicationEnvironment?: string,
  next = '/portfolio'
): string => {
  const base =
    applicationEnvironment === 'production'
      ? 'https://api.levelupsports.com'
      : 'https://level-up-backend-606592623818.us-central1.run.app';

  return `${base}/api/v1/auth/republic/start?next=${encodeURIComponent(next)}`;
};

/**
 * Where an athlete's page lives on LevelUp's site. Deliberately *plural* — the singular
 * `/athlete/` renders their SPA's 404 — and deliberately in code rather than in the stored
 * value, so the data stays a bare slug (decided on the ROS-833 plan). A second partner with a
 * different segment gets its own `<partner>-urls.ts` beside this one.
 */
export const LEVELUP_ATHLETE_SEGMENT = '/athletes/';

/**
 * The offering's page on LevelUp's own site, composed from the partner's site URL and the
 * offering's `partnerSlug`.
 *
 * `baseUrl` is the issuer's `platformUrl` (the dedicated partner-site field), with `websiteUrl` as
 * the caller's fallback while `platformUrl` is backfilled. Joined on the *origin*, not the raw
 * string: the monolith only forces a scheme onto these, so a value carrying a path
 * (`https://levelupsports.com/about`) would otherwise compose into `.../about/athletes/...`.
 * Undefined when either half is missing or the base does not parse — the caller falls back to
 * Republic's own offering page.
 */
export const levelupAthletePageUrl = (
  baseUrl: string | null | undefined,
  partnerSlug: string | null | undefined
): string | undefined => {
  if (!baseUrl || !partnerSlug) return undefined;

  try {
    const { origin } = new URL(baseUrl);
    return `${origin}${LEVELUP_ATHLETE_SEGMENT}${partnerSlug}`;
  } catch {
    return undefined;
  }
};
